// src/components/anlagen/AnlagenList.jsx
import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AuthContext from "../../context/AuthContext";
import AnlagenTable from "./AnlagenTable";
import { showSuccess, showError } from "../../utils/toastUtils";
import {
  Box,
  Heading,
  Stack,
  HStack,
  Input,
  Button,
  Text,
  Spinner,
} from "@chakra-ui/react";

// Keep your color-mode shim
import { useColorModeValue } from "../ui/color-mode";

export default function AnlagenList() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [anlagen, setAnlagen] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [searchProjekt, setSearchProjekt] = useState("");
  const [loading, setLoading] = useState(false);

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");
  const titleColor = useColorModeValue("blue.600", "blue.300");
  const subtleText = useColorModeValue("gray.600", "gray.300");
  const labelColor = useColorModeValue("white", "gray.200"); // label text white in light mode

  const fetchAnlagen = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/anlagen`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      // Accept common API shapes
      const data = res?.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.content)
          ? data.content
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.anlagen)
              ? data.anlagen
              : [];

      setAnlagen(list);

      console.log("Fetched:", list);

      if (res.status >= 400) {
        showError("Fehler!", `Anlagen konnten nicht geladen werden (HTTP ${res.status}).`);
      }
    } catch (error) {
      if (error.response) {
        console.error("API error:", error.response.status, error.response.data);
        showError("Fehler!", `Laden fehlgeschlagen (HTTP ${error.response.status}).`);
      } else if (error.request) {
        console.error("No response from server:", error.request);
        showError("Fehler!", "Keine Serverantwort. Prüfen Sie die API-URL / CORS.");
      } else {
        console.error("Request setup error:", error.message);
        showError("Fehler!", "Unbekannter Fehler beim Laden.");
      }
      setAnlagen([]);
    } finally {
      setLoading(false);
    }
  };

  const updateAnlage = async (id, updatedData) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${process.env.REACT_APP_API_URL}/api/anlagen/${id}`, updatedData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSuccess("Erfolg!", "Änderung gespeichert.");
      fetchAnlagen();
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      showError("Fehler!", "Speichern fehlgeschlagen.");
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/anlagen/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSuccess("Erfolg!", "Anlage gelöscht.");
      fetchAnlagen();
    } catch (error) {
      console.error("Fehler beim Löschen:", error);
      showError("Fehler!", "Löschen fehlgeschlagen.");
    }
  };

  useEffect(() => {
    fetchAnlagen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter
  const filteredAnlagen = (Array.isArray(anlagen) ? anlagen : []).filter((a) => {
    const nameFilter = searchName.trim().toLowerCase();
    const projektFilter = searchProjekt.trim().toLowerCase();
    return (
      (a?.anlagenName || "").toLowerCase().includes(nameFilter) &&
      (a?.projektNummer || "").toLowerCase().includes(projektFilter)
    );
  });

  const resetFilters = () => {
    setSearchName("");
    setSearchProjekt("");
  };

  return (
    <Stack spacing={6}>
      <HStack justify="space-between" align="baseline">
        <Heading as="h1" size="lg" color={titleColor}>
          Photovoltaikanlagen
        </Heading>
        {!loading && (
          <Text fontSize="sm" color={subtleText}>
            {filteredAnlagen.length} Ergebnis{filteredAnlagen.length === 1 ? "" : "se"}
          </Text>
        )}
      </HStack>

      {/* Filter Card */}
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        boxShadow="md"
        borderRadius="xl"
        p={{ base: 4, md: 6 }}
      >
        <HStack spacing={6} align="flex-end" flexWrap="wrap">
          <Box w={{ base: "100%", md: "260px" }}>
            <Text mb="1.5" fontWeight="medium" color={labelColor}>
              Filter nach Name
            </Text>
            <Input
              placeholder="z. B. Solarpark A"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              bg="blue.600"
              color="white"
              _placeholder={{ color: "whiteAlpha.800" }}
              borderColor="whiteAlpha.700"
              _hover={{ borderColor: "whiteAlpha.900" }}
              _focus={{ bg: "blue.700", borderColor: "white", color: "white" }}
            />
          </Box>

          <Box w={{ base: "100%", md: "260px" }}>
            <Text mb="1.5" fontWeight="medium" color={labelColor}>
              Filter nach Projekt-Nr.
            </Text>
            <Input
              placeholder="z. B. PRJ-123"
              value={searchProjekt}
              onChange={(e) => setSearchProjekt(e.target.value)}
              bg="blue.600"
              color="white"
              _placeholder={{ color: "whiteAlpha.800" }}
              borderColor="whiteAlpha.700"
              _hover={{ borderColor: "whiteAlpha.900" }}
              _focus={{ bg: "blue.700", borderColor: "white", color: "white" }}
            />
          </Box>

          <Button onClick={resetFilters} variant="outline">
            Filter zurücksetzen
          </Button>
        </HStack>
      </Box>

      {/* Table Card */}
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        boxShadow="md"
        borderRadius="xl"
        p={0}
        overflow="hidden"
      >
        {loading ? (
          <HStack justify="center" py={12}>
            <Spinner size="lg" borderWidth="4px" color="blue.500" />
          </HStack>
        ) : (
          <Box overflowX="auto">
            <AnlagenTable
              anlagen={filteredAnlagen}
              user={user}
              onDelete={handleDelete}
              onUpdate={updateAnlage}
              onView={(anlage) => navigate(`/anlagen/${anlage.id}`)}
              onEdit={(anlage) => navigate(`/anlagen/${anlage.id}/edit`)}
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
}